import type {
  PdfElement,
  SetTableTag,
} from '../../kidsV2/types/KidPdfGeneratorV2.types';
import { isPresetTableInjectedCell } from '../../kidsV2/components/inputs/presetTables/presetInjectedCells';
import { transformElementsToKidDataModels } from '../../kidsV2/utils/kidDataTransformers';

export type SaveComparisonContext = {
  clientId: string;
  fundId: string;
  shareClassId: string;
  kidId: string;
  kidIdCreateDate: string;
};

const VOLATILE_SAVE_MODEL_KEYS = new Set([
  'kid_create_year',
  'client_id',
  'fund_id',
  'share_class_id',
  'kid_id',
  'kid_index',
  'field_id',
  'last_updating_timestamp',
  'last_updated_by',
  'kid_id_create_date',
  // Marker seed rewrites these on load; ignore so open ≠ dirty.
  'existing_markers',
]);

const SET_TABLE_TAGS = new Set<string>([
  'costs_over_time_table',
  'composition_of_costs_table',
  'performance_scenarios_table',
]);

/**
 * Blank auto-filled marker cells (and SRI content) so marker seed on load
 * does not make a freshly opened document look unsaved.
 */
function stripAutoFilledForComparison(elements: PdfElement[]): PdfElement[] {
  return elements.map((element) => {
    if (element.tag === 'sri_chart') {
      const { existing_markers: _markers, ...rest } = element;
      return { ...rest, content: '' };
    }

    if (
      SET_TABLE_TAGS.has(element.tag) &&
      Array.isArray(element.content)
    ) {
      const tag = element.tag as SetTableTag;
      const content = (element.content as string[][]).map((row, rowIndex) =>
        row.map((cell, colIndex) =>
          isPresetTableInjectedCell(tag, rowIndex, colIndex) ? '' : cell,
        ),
      );
      const { existing_markers: _markers, ...rest } = element;
      return { ...rest, content };
    }

    const { existing_markers: _markers, ...rest } = element;
    return rest as PdfElement;
  });
}

const normalizeForComparison = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeForComparison(item));
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entryValue]) => entryValue !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, entryValue]) => [key, normalizeForComparison(entryValue)]);

    return Object.fromEntries(entries);
  }

  return value;
};

const buildComparableModels = (
  elements: PdfElement[] | undefined,
  context: SaveComparisonContext,
) => {
  const transformed = transformElementsToKidDataModels(
    stripAutoFilledForComparison(elements || []),
    context.clientId,
    context.fundId,
    context.shareClassId,
    context.kidId,
    context.clientId, // lastUpdatedBy: use clientId for comparison (no specific user)
    context.kidIdCreateDate,
  );

  return transformed.map((model) => {
    const entries = Object.entries(model).filter(
      ([key, value]) =>
        !VOLATILE_SAVE_MODEL_KEYS.has(key) && value !== undefined,
    );

    return normalizeForComparison(Object.fromEntries(entries));
  });
};

export const createComparableSaveSignature = (
  elements: PdfElement[] | undefined,
  context: SaveComparisonContext,
): string => {
  return JSON.stringify(
    normalizeForComparison(buildComparableModels(elements, context)),
  );
};

export type SaveComparisonChange = {
  path: string;
  before: unknown;
  after: unknown;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const diffValues = (
  beforeValue: unknown,
  afterValue: unknown,
  path: string,
  changes: SaveComparisonChange[],
) => {
  if (Array.isArray(beforeValue) && Array.isArray(afterValue)) {
    const maxLen = Math.max(beforeValue.length, afterValue.length);
    for (let i = 0; i < maxLen; i++) {
      diffValues(beforeValue[i], afterValue[i], `${path}[${i}]`, changes);
    }
    return;
  }

  if (isPlainObject(beforeValue) && isPlainObject(afterValue)) {
    const keys = new Set([
      ...Object.keys(beforeValue),
      ...Object.keys(afterValue),
    ]);
    [...keys]
      .sort((a, b) => a.localeCompare(b))
      .forEach((key) => {
        const nextPath = path ? `${path}.${key}` : key;
        diffValues(beforeValue[key], afterValue[key], nextPath, changes);
      });
    return;
  }

  if (beforeValue !== afterValue) {
    changes.push({
      path: path || 'root',
      before: beforeValue,
      after: afterValue,
    });
  }
};

export const getSaveComparisonChanges = (
  initialElements: PdfElement[] | undefined,
  currentElements: PdfElement[] | undefined,
  context: SaveComparisonContext,
): SaveComparisonChange[] => {
  const initialComparable = normalizeForComparison(
    buildComparableModels(initialElements, context),
  );
  const currentComparable = normalizeForComparison(
    buildComparableModels(currentElements, context),
  );

  const changes: SaveComparisonChange[] = [];
  diffValues(initialComparable, currentComparable, '', changes);
  return changes;
};
